import { useTranslation } from '@/lib/i18n';
import { Select } from '@/components/ui';
import { useBrandsQuery, type BrandItem } from '../hooks/useBrandsQuery';

/**
 * R13 A-2: 상품 폼 브랜드 선택기.
 *
 * `GET /api/v1/catalog/brands` 로 브랜드 목록을 채우고, 선택한 brandId(string)를 onChange 로 올려
 * detailMapper 가 `POST/PUT /admin/v1/products` 의 brandId 필드로 전송하게 한다 → brand_id 영속
 * → 상세/미리보기 ProductDetailView.brandName 노출(A-2 회귀 해소).
 *
 * 브랜드 수가 적어 페이지네이션 없는 단일 select 로 충분하다. 빈 옵션(value='')=미선택(brandId=null).
 * 라이브 brands 엔드포인트는 be-catalog 재배포 후 동작하며, 그전엔 빈 목록이라 미선택 상태로 안전하게
 * 동작한다(detailMapper 가 brandId 미전송 시 omit → 기존 round-trip 영향 없음).
 *
 * 편집 진입 시 detailToFormValues 가 복원한 brandId 가 현재 brands 목록에 아직 없을 수 있어(staleTime
 * 내 미반영 등) <option> 으로 떠 있도록 selected 값을 1회성 옵션으로 보강한다(빈 select 회피).
 */

export interface BrandSelectorProps {
  value: string | null;
  disabled?: boolean;
  errorMessage?: string | null | undefined;
  onChange: (brandId: string | null) => void;
  testId?: string;
}

export const BrandSelector = ({
  value,
  disabled,
  errorMessage,
  onChange,
  testId = 'product-brand-selector',
}: BrandSelectorProps) => {
  const { t } = useTranslation('products');
  const query = useBrandsQuery();
  const brands = query.data ?? [];

  // 현재 선택값이 목록에 없으면(아직 로드 전/타 브랜드) 1회성 옵션으로 추가해 select 가 빈칸이 되지 않게.
  const hasSelected = value != null && brands.some((b) => b.brandId === value);
  const extraOption: BrandItem | null =
    value != null && !hasSelected
      ? { brandId: value, brandName: value, slug: '' }
      : null;

  return (
    <div className="flex flex-col gap-1" data-testid={testId}>
      <Select
        value={value ?? ''}
        disabled={disabled || query.isPending}
        aria-label={t('products.fields.brand', '브랜드')}
        data-testid={`${testId}-select`}
        onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
      >
        <option value="">
          {query.isPending
            ? t('products.fields.brandLoading', '브랜드 불러오는 중…')
            : t('products.fields.brandUnset', '브랜드 미선택')}
        </option>
        {extraOption ? (
          <option value={extraOption.brandId}>{extraOption.brandName}</option>
        ) : null}
        {brands.map((b) => (
          <option key={b.brandId} value={b.brandId}>
            {b.brandName}
          </option>
        ))}
      </Select>

      {query.isError ? (
        <span className="text-xs" style={{ color: 'var(--signal-danger)' }}>
          {t('products.fields.brandError', '브랜드 목록을 불러오지 못했습니다.')}
        </span>
      ) : null}

      {errorMessage ? (
        <span className="text-xs" style={{ color: 'var(--signal-danger)' }}>
          {errorMessage}
        </span>
      ) : null}
    </div>
  );
};
